import React from 'react';
import moment from 'moment';
import IntervalRenderer from 'flow/components/custom-components/interval-renderer';
import { getPrettyDuration } from 'flow/utils/call-utils';
import ButtonWithTitle from 'flow/components/communication-canvas/buttons/ButtonWithTitle';
import useAppContext from 'flow/hooks/useAppContext';
import { twMerge } from 'tailwind-merge';

interface MeetingSmallCallItemProps {
    memberInChat?: boolean;
    chatName?: string;
    meetingCallStartTime?: Date;
    numberOfMembers?: number;
    displayNames?: string;
    isOnHold?: boolean;
    doHangup?: () => void;
    onSwap?: () => void;
    isMinimized?: boolean;
}

const MeetingSmallCallItem: React.FC<MeetingSmallCallItemProps> = ({
    memberInChat,
    chatName,
    meetingCallStartTime,
    numberOfMembers,
    displayNames,
    isOnHold,
    doHangup,
    onSwap,
    isMinimized,
}) => {
    const { i18next } = useAppContext();

    return (
        <div
            className={twMerge(
                'group m-1.5 flex flex-row justify-start rounded text-white',
                isOnHold ? 'bg-gray-800' : 'bg-gray-700',
            )}
            data-test-id={'meeting-small-call-item'}
        >
            <div className="m-3 flex h-12 w-12 items-center justify-center rounded-xl bg-purple-500 text-lg">
                {numberOfMembers}
            </div>

            <div>
                <div className="mt-5 text-base font-normal">
                    {memberInChat && chatName
                        ? chatName
                        : i18next.t('videoconference.meeting')}
                </div>
                <div className="text-sm text-gray-400">{displayNames}</div>
            </div>

            <div
                className={twMerge(
                    'ms-auto me-3 flex items-center font-semibold',
                    isMinimized && 'ms-3',
                    isOnHold
                        ? 'group-focus-visible-within:hidden group-hover:hidden'
                        : 'group-hover:flex',
                )}
            >
                {isOnHold ? (
                    i18next.t('callcontrol.parked_call')
                ) : (
                    <IntervalRenderer interval={1000}>
                        {() =>
                            getPrettyDuration(
                                moment.duration(
                                    moment().diff(moment(meetingCallStartTime)),
                                ),
                            )
                        }
                    </IntervalRenderer>
                )}
            </div>

            <div
                className={twMerge(
                    'ms-auto items-center',
                    isOnHold
                        ? 'sr-only-until-hover-or-focus flex'
                        : 'flex group-hover:hidden',
                )}
            >
                <ButtonWithTitle
                    buttonTitle={i18next.t('Call.Canvas.Title.HangUp')}
                    iconKey="call_end"
                    onClick={doHangup}
                    dataTestId="hangup-button"
                />

                <ButtonWithTitle
                    buttonTitle={i18next.t('Call.Canvas.Title.Swap')}
                    iconKey="swapCalls"
                    onClick={onSwap}
                    dataTestId="swap-button"
                />
            </div>
        </div>
    );
};

export default MeetingSmallCallItem;
